import { isArray, isEmpty, isString } from 'radash';
import { memo, useCallback, useContext, useMemo } from 'react';

import { FocusAttributeContext } from '@/dialogs/item-editor/Context/FocusAttributeContext';
import { getInjector } from '@/services/angularjs-bridge';
import {DataModelAttributeBaseClass, DataModelAttributeRenderer, DataModelAttributeTypeName } from '@/types/DataModel';

import {
    AdditionalCategoryComponent,
    AssetComponent,
    BooleanComponent,
    CollectionComponent,
    DateComponent,
    DateTimeComponent,
    DimensionalComponent,
    DocumentComponent,
    EnumComponent,
    FloatComponent,
    GroupComponent,
    HTMLComponent,
    ImageComponent,
    IntegerComponent,
    MultiDimensionalComponent,
    MultiReferenceComponent,
    PasswordComponent,
    PhysicalComponent,
    SingleReferenceComponent,
    StringComponent,
    TextareaComponent,
} from '.';
import { CustomTemplate } from './CustomTemplate/CustomTemplate';
import { GroupAttributeLabelContextProvider } from './Group/GroupAttributeLabelContext';
import { EditorComponent } from './types';

/**
 * An attribute component wrapper, give it the attribute definition and it will render the appropriate
 * component for the attribute.
 */
const CoreAttributeComponent = ({ attribute, isKeyAttribute, primaryKey, groupLabelType, disableUrlSync, ...props }: EditorComponent) => {
    // Important: this component often wraps editors that may render `null` when hidden.
    // If we wrap them in a normal <div>, that empty div still becomes a flex item, and
    // parent containers using `gap` will show spacing for "invisible" attributes.
    // `tw-contents` makes the wrapper not generate a layout box, avoiding phantom gaps.
    const className = 'tw-contents';
    const setPinnedAttributeName = useContext(FocusAttributeContext)?.setPinnedAttributeName;

    // Memoize the type and renderer logic to prevent unnecessary recalculations
    const { type, renderer , template} = useMemo(() => {
        const type = attribute.typeName || attribute.baseClass;
        let renderer = attribute.params?.renderer || attribute.params?.customRenderer;
        const rendererParams: { [key: string]: string | number } = {};

        const $angularJsInjector = getInjector();
        const $templateCache = $angularJsInjector.get('$templateCache');
        let template;
        if (isArray(renderer)) {
            renderer.forEach(renderingParam => rendererParams[renderingParam.key] = renderingParam.value);
        } else if (isString(renderer)) {
            template= $templateCache.get(renderer);
        }
        if (!isEmpty(rendererParams)) {
            if (!isEmpty(rendererParams['type'])) {
                renderer = rendererParams['type'] as string;
            } else {
                // A `renderer` object was defined, but it has no `type` so we fallback to the attribute type
                renderer = undefined;
            }
        }
        return { type, renderer, template};
    }, [attribute.typeName, attribute.baseClass, attribute.params?.renderer, attribute.params?.customRenderer]);
    const handleUrlChange = useCallback(() => {
        // Mass-update reuses these editors on the browse route; without this guard,
        // focus rewrites the URL to /edit/new/attribute/... (no primaryKey).
        if (disableUrlSync) {
            return;
        }
        setPinnedAttributeName?.(attribute.name);

        const $injector = getInjector();
        const location = $injector.get('$location');
        const $timeout = $injector.get('$timeout');

        const currentPath = location?.path();
        let url: string;

        if (currentPath.includes('/copy/')) {
            const copyMatch = currentPath.match(/\/copy\/([^/]+)/);
            if (copyMatch) {
                url = `/copy/${copyMatch[1]}/attribute/${attribute.name}`;
            } else {
                url = `/edit/new/attribute/${attribute.name}`;
            }
        } else if (primaryKey) {
            url = `/edit/${primaryKey}/attribute/${attribute.name}`;
        } else {
            url = `/edit/new/attribute/${attribute.name}`;
        }

        $timeout(() => {
            location.displayUrl(url);
        });
    }, [disableUrlSync, primaryKey, attribute.name, setPinnedAttributeName]);

    // Memoize the component rendering to prevent unnecessary re-renders
    const component = useMemo(() => {

        if (renderer) {
            switch (renderer) {
                case DataModelAttributeRenderer.SingleBoolean:
                    return (
                        <div className={className}>
                            <BooleanComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                        </div>
                    );
                case DataModelAttributeRenderer.HTMLInput:
                    return (
                        <div className={className}>
                            <HTMLComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                        </div>
                    );
                case DataModelAttributeBaseClass.Group:
                    return (
                        <div className={className}>
                            <GroupComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props}/>
                        </div>
                    );
            }
        }
        if (template) {
            const isStringType =
                attribute.typeName === DataModelAttributeTypeName.String ||
                attribute.baseClass === DataModelAttributeBaseClass.String;
            return (
                <div className={className}>
                    <CustomTemplate
                        attribute={attribute}
                        template={template}
                        hideLabel={!isStringType}
                        {...props}
                    />
                </div>
            );
        }
        switch (type) {
            case DataModelAttributeTypeName.String:
                return (
                    <div className={className}>
                        <StringComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} handleUrlChange={handleUrlChange}/>
                    </div>
                );

            case DataModelAttributeBaseClass.Integer:
                return (
                    <div className={className}>
                        <IntegerComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} handleUrlChange={handleUrlChange}/>
                    </div>
                );

            case DataModelAttributeBaseClass.Float:
            case DataModelAttributeTypeName.Decimal:
                return (
                    <div className={className}>
                        <FloatComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} handleUrlChange={handleUrlChange}/>
                    </div>
                );

            case DataModelAttributeTypeName.Boolean:
                return (
                    <div className={className}>
                        <BooleanComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.Enum:
            case DataModelAttributeTypeName.EnumSet:
            case DataModelAttributeTypeName.OpenEnum:
            case DataModelAttributeTypeName.OpenEnumSet:
            case DataModelAttributeTypeName.Codelist:
            case DataModelAttributeTypeName.OpenCodelist:
            case DataModelAttributeTypeName.CodelistSet:
            case DataModelAttributeTypeName.OpenCodelistSet:
                return (
                    <div className={className}>
                        <EnumComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.Date:
                return (
                    <div className={className}>
                        <DateComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} handleUrlChange={handleUrlChange}/>
                    </div>
                );

            case DataModelAttributeTypeName.DateTime:
                return (
                    <div className={className}>
                        <DateTimeComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.TextArea:
                return (
                    <div className={className}>
                        <TextareaComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.Password:
                return (
                    <div className={className}>
                        <PasswordComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.Image:
                return (
                    <div className={className}>
                        <ImageComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.Document:
                return (
                    <div className={className}>
                        <DocumentComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.Asset:
                return (
                    <div className={className}>
                        <AssetComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.Collection:
                return (
                    <div className={className}>
                        <CollectionComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.MultiReference:
                return (
                    <div className={className}>
                        <MultiReferenceComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} />
                    </div>
                );

            case DataModelAttributeTypeName.SingleReference:
                return (
                    <div className={className}>
                        <SingleReferenceComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} handleUrlChange={handleUrlChange}/>
                    </div>
                );

            case DataModelAttributeTypeName.Group:
                return (
                    <div className={className}>
                        <GroupComponent attribute={attribute} {...props} isKeyAttribute={isKeyAttribute} />
                    </div>
                );

            case DataModelAttributeTypeName.Dimensional:
                return (
                    <div className={className}>
                        <DimensionalComponent attribute={attribute} {...props} isKeyAttribute={isKeyAttribute} />
                    </div>
                );

            case DataModelAttributeTypeName.MultiDimensional:
                return (
                    <div className={className}>
                        <MultiDimensionalComponent attribute={attribute} {...props} isKeyAttribute={isKeyAttribute} />
                    </div>
                );

            case DataModelAttributeTypeName.Physical:
                return (
                    <div className={className}>
                        <PhysicalComponent attribute={attribute} {...props} isKeyAttribute={isKeyAttribute} handleUrlChange={handleUrlChange}/>
                    </div>
                );

            case DataModelAttributeTypeName.AdditionalCategory:
                return (
                    <div className={className}>
                        <AdditionalCategoryComponent attribute={attribute} {...props} isKeyAttribute={isKeyAttribute} />
                    </div>
                );

            default:
                return (
                    <div className={className}>
                        <StringComponent attribute={attribute} isKeyAttribute={isKeyAttribute} {...props} handleUrlChange={handleUrlChange}/>
                    </div>
                );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderer, type, attribute, isKeyAttribute, props, handleUrlChange, className]);

    return (
        <GroupAttributeLabelContextProvider value={groupLabelType}>
            {component}
        </GroupAttributeLabelContextProvider>
    );
};

// Memoize the component to prevent unnecessary re-renders
export const AttributeComponent = memo(CoreAttributeComponent);
